import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import AnalysisTable from '../shared/AnalysisTable';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useProductAnalysis } from '../../../hooks/analysis/useProductAnalysis';
import { Granularity } from '../../../utils/analytics/types';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const FEE_SERIES: Array<{ key: 'adult' | 'child' | 'pet' | 'vehicle'; label: string }> = [
  { key: 'adult', label: 'Adult' },
  { key: 'child', label: 'Child' },
  { key: 'pet', label: 'Pet' },
  { key: 'vehicle', label: 'Vehicle' },
];

const ProductAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [granularity, setGranularity] = useState<Granularity>('month');
  const data = useProductAnalysis(range, granularity);

  const popularityColumns = [
    { key: 'siteAlias', label: 'Site' },
    { key: 'bookings', label: 'Bookings', align: 'right' as const },
    { key: 'nights', label: 'Nights', align: 'right' as const },
    { key: 'revenue', label: 'Revenue', align: 'right' as const, format: formatCurrency },
  ];

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <div className="analysis-toggle-group" role="group" aria-label="Granularity">
          {(['week', 'month'] as Granularity[]).map(g => (
            <button key={g} className={granularity === g ? 'active' : ''} onClick={() => setGranularity(g)}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <KpiRow>
        <KpiTile
          label="Median stay"
          value={data.medianLos !== null ? `${data.medianLos} nights` : '—'}
        />
        <KpiTile
          label="Top site"
          value={data.topSite?.siteAlias ?? '—'}
          note={data.topSite ? formatCurrency(data.topSite.revenue) : ''}
        />
        <KpiTile
          label="Post-booking site adds"
          value={data.siteAddRate !== null ? formatPercentage(data.siteAddRate) : '—'}
          note="stays adding a site later"
        />
        <KpiTile
          label="Est. overage fees"
          value={data.hasFeeConfig ? formatCurrency(data.feeTotal) : '—'}
          note="estimated from fee config"
        />
      </KpiRow>

      <ChartPanel
        title="Revenue by site type"
        subtitle="Per-site revenue over time, one line per configured site type"
        loading={data.loading}
        error={data.error}
        empty={data.typeTrend.data.length === 0 || data.typeTrend.series.length === 0}
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            ...data.typeTrend.series.map(name => ({
              key: name,
              label: name,
              align: 'right' as const,
              format: (v: any) => formatCurrency(Number(v ?? 0)),
            })),
          ],
          rows: data.typeTrend.data,
        }}
        csv={{
          filename: `site-type-revenue-${range.from}-${range.to}`,
          columns: [
            { key: 'label', label: 'Period' },
            ...data.typeTrend.series.map(name => ({ key: name, label: name })),
          ],
          rows: data.typeTrend.data,
        }}
      >
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.typeTrend.data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={70} />
            <Tooltip formatter={(value: any, name: any) => [formatCurrency(Number(value)), name]} />
            <Legend />
            {data.typeTrend.series.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>

      <div className="chart-panel-grid">
        <ChartPanel
          title="Length of stay"
          loading={data.loading}
          error={data.error}
          empty={data.losBuckets.every(b => b.count === 0)}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.losBuckets} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
              <Tooltip formatter={(value: any) => [value, 'Stays']} />
              <Bar dataKey="count" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Estimated overage fee revenue"
          subtitle="Reconstructed from fee config × party counts — estimate only"
          loading={data.loading}
          error={data.error}
          empty={!data.hasFeeConfig || data.feePoints.every(p => p.adult + p.child + p.pet + p.vehicle === 0)}
          emptyMessage={data.hasFeeConfig ? 'No estimated fees in this range.' : 'No active overage fees configured.'}
          footnote="Reservations don't itemize fee lines; exact figures need a cost-breakdown endpoint."
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.feePoints} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
              <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={64} />
              <Tooltip formatter={(value: any, name: any) => [formatCurrency(Number(value)), name]} />
              <Legend />
              {FEE_SERIES.map((series, i) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  stackId="fees"
                  fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <ChartPanel
        title="Site popularity"
        subtitle="Ranked by per-site revenue in the selected range"
        loading={data.loading}
        error={data.error}
        empty={data.popularity.length === 0}
        csv={{
          filename: `site-popularity-${range.from}-${range.to}`,
          columns: popularityColumns.map(({ key, label }) => ({ key, label })),
          rows: data.popularity,
        }}
      >
        <AnalysisTable columns={popularityColumns} rows={data.popularity} defaultSortKey="revenue" maxRows={25} />
      </ChartPanel>
    </div>
  );
};

export default ProductAnalysis;
